package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.{AccountSubjectResponse, CCABServer}
import com.neurobs.errors.{Result, toResult}
import com.neurobs.react.{CircuitState, ControlWrapperFactory, ControlWrapperFactoryReact, Edit, EditControlWrapper, FormDialog, FormProcessResultValue, GapSize, HoriAlign, LTFullRow, LayoutTable, ListViewAdapter, ListView, ListViewState}
import diode.ActionResult.{ModelUpdate, NoChange}
import diode._
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.useEffect
import slinky.core.facade.ReactElement
import slinky.web.html.{className, span}

import scala.concurrent.ExecutionContext.Implicits.global
import scala.concurrent.Future

/**
 * Handles a dialog containing a list of subjects for selection.
 */
object SubjectSelectDialog {

	type ElementType = AccountSubjectResponse

	object Adapter extends ListViewAdapter[ElementType, ElementType] {
		override def header: Seq[String] = Seq("Last", "First", "Birthdate", "Local ID", "Reporting ID")
		override def makeRow(t: ElementType): Seq[String] = {
			t.identity match {
				case None => Seq("", "", "", "", t.reportingId.noBracketsString)
				case Some(i) =>
					Seq(
						i.lastName.getOrElse(""),
						i.firstName.getOrElse(""),
						i.birthSummary,
						i.localID.getOrElse(""),
						t.reportingId.noBracketsString
					)
			}
		}
		override def makeValue(t: ElementType): ElementType = t
	}

	case class Model
	(
		loading: Boolean = true,
		loadingError: String = "",
		lastNameSearch: EditControlWrapper,
		listScrollCounter: Int = 0,
		list: ListViewState[ElementType, ElementType] = new ListViewState[ElementType, ElementType](Adapter, Seq(), None)
	)

	case class SetSubjects(subjects: Result[Seq[ElementType]]) extends Action
	case object LastNameSearchUpdate extends Action
	case class SetSelection(sel: Option[Int]) extends Action

	class AppCircuit(val initialModel: Model) extends Circuit[Model] {
		override val actionHandler: HandlerFunction = (model, action) => action match {
			case SetSelection(sel) =>
				Some(ModelUpdate(model.copy(list = model.list.newSelection(sel))))
			case SetSubjects(res) =>
				val newModel = res match {
					case Left(error) => model.copy(loading = false, loadingError = error.description)
					case Right(subjects) =>
						val sorted = subjects.sortWith(_.identity.flatMap(_.lastName).getOrElse("") < _.identity.flatMap(_.lastName).getOrElse(""))
						model.copy(loading = false, list = model.list.newItems(sorted))
				}
				Some(ModelUpdate(newModel))
			case LastNameSearchUpdate =>
				val name = model.lastNameSearch.getValue()
				if (name.isEmpty) Some(NoChange)
				else {
					val item = model.list.items.find(_.identity.exists(_.lastName.exists(_.startsWith(name))))
					item match {
						case None => Some(ModelUpdate(model.copy(list = model.list.newSelection(None))))
						case Some(subject) =>
							val newModel = model.copy(listScrollCounter = model.listScrollCounter + 1, list = model.list.newSelection(subject))
							Some(ModelUpdate(newModel))
					}
				}
			case _ => None
		}
	}

	def initialModel(implicit factory: ControlWrapperFactory): Model = Model(lastNameSearch = factory.edit(initialValue = ""))

	@react object View {
		private val searchBoxEmWidth = 10
		private val subjectListEmMaxHeight = 20
		private val errorClass = "structure-status-error"

		case class Props
		(
			loadSubjects: () => Future[Result[Seq[ElementType]]],
			onSelect: Option[ElementType] => Unit
		)

		val component = FunctionalComponent[Props] { props =>
			val state = CircuitState[Model](() => initialModel(ControlWrapperFactoryReact), new AppCircuit(_))

			useEffect(() => {
				props.loadSubjects().map(r => state.dispatch(SetSubjects(r)))
			}, Vector())

			val custom: ReactElement =
				if (state.value.loading) span("Loading subjects...")
				else if (state.value.loadingError.nonEmpty) span(className := errorClass)(state.value.loadingError)
				else LayoutTable(gapSize = GapSize.Large, fullWidth = true)(
					LTFullRow(HoriAlign.Left)(
						"Last name search: ",
						Edit(
							state = state.value.lastNameSearch,
							emWidth = searchBoxEmWidth,
							onChange = Some(() => state.dispatch(LastNameSearchUpdate))
						)
					),
					LTFullRow(HoriAlign.Left)(
						ListView(
							props = state.value.list.generateProperties(),
							onSelectionChange = i => state.dispatch(SetSelection(i)),
							emMaxHeight = Some(subjectListEmMaxHeight),
							scrollToSelectionCounter = state.value.listScrollCounter
						)
					)
				)
			FormDialog(
				submitButtonCaption = "Select",
				submitButtonEnabled = state.value.list.selectIndex.isDefined,
				customView = Some(custom),
				process = () => toResult(state.value.list.value.map(v => Left(FormProcessResultValue(v))), noValueMessage = "No subject is selected"),
				onFormCancel = Some(() => props.onSelect(None)),
				onSuccess = res => props.onSelect(Some(res.asInstanceOf[FormProcessResultValue[ElementType]].value))
			)
		}
	}
}
